<script setup lang="ts">
import CheckableTag from '@pages/search/components/filters/CheckableTag.vue';
import { trans } from '@tools/translation.ts';

interface Option {
  code: string;
  exponent?: string;
  label?: string;
}

interface CheckboxGroupFilterProps {
  selected?: string[];
  options: (string | Option)[];
  translationPrefix?: string;
}

const { selected = [], options, translationPrefix } = defineProps<CheckboxGroupFilterProps>();

const emit = defineEmits<{(e: 'update:selected', value: string[]): void}>();

const toggleOption = (code: string) => {
  const newSelected = [ ...selected ];
  const index = newSelected.indexOf(code);
  if (index > -1) {
    newSelected.splice(index, 1);
  } else {
    newSelected.push(code);
  }
  emit('update:selected', newSelected);
};

const isSelected = (code: string): boolean => selected.includes(code);

const getOptionCode = (option: string | Option): string => (typeof option === 'string' ? option : option.code);

const getOptionExponent = (option: string | Option): string | undefined => (typeof option === 'string' ? undefined : option.exponent);

const getOptionLabel = (option: string | Option): string => {
  if (typeof option !== 'string' && option.label) {
    return option.label;
  }

  const code = getOptionCode(option);
  return translationPrefix ? trans(`${translationPrefix}.${code}`) : code;
};
</script>

<template>
  <div class="flex flex-wrap gap-4">
    <div
      v-for="option in options"
      :key="getOptionCode(option)"
      class="checkbox-group-filter__item"
    >
      <CheckableTag
        :content="getOptionLabel(option)"
        :exponent="getOptionExponent(option)"
        :value="getOptionCode(option)"
        :active="isSelected(getOptionCode(option))"
        @update:tag="toggleOption"
      />
    </div>
  </div>
</template>
